/**
 * Dates. Stored in UTC, shown in IST (ARCH-002 §1) — the conversion happens here and nowhere else, so when
 * a date is a day out there is one file to read.
 */

/** India Standard Time: UTC+5:30, and no daylight saving, ever. */
export const IST_OFFSET_MINUTES = 5 * 60 + 30;

export const IST_TIME_ZONE = 'Asia/Kolkata';

/** A calendar date with no time and no zone — what a ledger entry is actually dated. */
export interface CalendarDate {
  readonly year: number;
  /** 1–12, not the 0–11 the `Date` constructor uses. */
  readonly month: number;
  readonly day: number;
}

/** The IST calendar date an instant falls on. */
export function toIstDate(utc: Date): CalendarDate {
  const shifted = new Date(utc.getTime() + IST_OFFSET_MINUTES * 60_000);

  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/**
 * The hour of the IST day an instant falls in, 0–23.
 *
 * `LED-01`'s greeting is the only thing that asks (`FEAT-011 §2.1`), and it asks about the *reader's*
 * morning, not the server's. Built the same way as {@link toIstDate}, from a fixed offset rather than a
 * zone database, because IST has never had daylight saving and never will.
 */
export function istHour(utc: Date): number {
  return new Date(utc.getTime() + IST_OFFSET_MINUTES * 60_000).getUTCHours();
}

/** Midnight IST on a calendar date, as the instant to store. */
export function fromIstDate(date: CalendarDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day) - IST_OFFSET_MINUTES * 60_000);
}

/** `27 Aug 2026` — the way the screen book writes a date. */
export function formatIstDate(utc: Date): string {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(utc);
}

/**
 * `Sun, 16 Aug` — a date with its weekday and no year, as `LED-01`'s greeting writes it.
 *
 * No year, because the greeting is about today and a year on it would be the only part nobody reads. The
 * weekday is there because a person checking their spending on a Sunday evening is doing something
 * different from one checking it on a Wednesday morning, and the screen book says so.
 */
export function formatIstWeekdayDate(utc: Date): string {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_TIME_ZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(utc);
}

/**
 * `23 Nov 2026` from a plain `yyyy-MM-dd`, with no instant and no zone in between.
 *
 * `STD-003 §1` sends a date that is a date — `occurredOn`, `trialEndsOn` — as `yyyy-MM-dd` precisely
 * because it is not an instant. Putting one through `new Date()` invents a midnight, and a midnight put
 * through a timezone is a day that can move: `2026-11-23` read as UTC and shown in a zone behind it is the
 * 22nd. So this never builds a `Date` at all.
 *
 * Returns the input unchanged if it is not a date, because a screen showing the raw value is better than a
 * screen showing "Invalid Date".
 */
export function formatCalendarDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    return iso;
  }

  const [, year, month, day] = match;

  return `${String(Number(day))} ${MONTHS[Number(month) - 1] ?? month} ${year}`;
}

/** Short month names, as the screen book writes them. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** `9:30 am` — lower case, as the screen book writes a time. */
export function formatIstTime(utc: Date): string {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(utc)
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * The month a date belongs to when a person's month starts on their payday (`ONB-05`,
 * `User.MonthStartsOnDay`).
 *
 * If your month starts on the 25th, the 24th of August still belongs to July's month and the 25th begins
 * August's. Getting this wrong files a salary credit under the wrong month, which is the most visible
 * arithmetic mistake this product could make.
 */
export function paydayMonth(
  date: CalendarDate,
  monthStartsOnDay: number,
): { year: number; month: number } {
  assertStartDay(monthStartsOnDay);

  if (date.day >= monthStartsOnDay) {
    return { year: date.year, month: date.month };
  }

  return date.month === 1
    ? { year: date.year - 1, month: 12 }
    : { year: date.year, month: date.month - 1 };
}

/** The first and last IST dates of a payday month, inclusive. */
export function paydayMonthRange(
  year: number,
  month: number,
  monthStartsOnDay: number,
): { start: CalendarDate; end: CalendarDate } {
  assertStartDay(monthStartsOnDay);

  const start: CalendarDate = { year, month, day: monthStartsOnDay };
  const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  const dayBeforeNextStart = new Date(
    fromIstDate({ year: next.year, month: next.month, day: monthStartsOnDay }).getTime() - 24 * 60 * 60_000,
  );

  return { start, end: toIstDate(dayBeforeNextStart) };
}

/**
 * The payday month a date falls in, and the *n* before it — newest first, as `yyyy-MM`.
 *
 * `LED-05`'s month control (`FEAT-011 §2.1`). The list is arithmetic rather than a request: the payday
 * rule is already on the client, and an endpoint answering "which months may I ask for" would be a round
 * trip to be told what {@link paydayMonth} already knows. Months with no entries are in the list like any
 * other — leaving them out would mean asking the server which months are empty, and would make the
 * control's contents change every time the filter beside it did.
 *
 * `yyyy-MM` because that is what `GET /transactions?month=` takes (`FEAT-011 §5.4`), and the server
 * resolves it to the payday window from the same `monthStartsOnDay` this used.
 */
export function recentPaydayMonths(
  today: CalendarDate,
  monthStartsOnDay: number,
  count: number,
): readonly string[] {
  const current = paydayMonth(today, monthStartsOnDay);
  const months: string[] = [];

  for (let back = 0; back < count; back += 1) {
    // Counting in months from a `{year, month}` pair rather than stepping a `Date` by 30 days, which
    // lands in the wrong month twice a year and in the wrong year once.
    const total = current.year * 12 + (current.month - 1) - back;

    months.push(`${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, '0')}`);
  }

  return months;
}

/** `August 2026`, for a month heading. */
export function formatMonth(year: number, month: number): string {
  return new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  );
}

function assertStartDay(monthStartsOnDay: number): void {
  if (!Number.isInteger(monthStartsOnDay) || monthStartsOnDay < 1 || monthStartsOnDay > 28) {
    throw new RangeError(
      'monthStartsOnDay is 1–28 (ARCH-002 §2). 29, 30 and 31 do not exist in every month, so a cycle ' +
        'starting on one of them would skip February.',
    );
  }
}
